import axios from "axios";
import dayjs from "dayjs";
import db from "../database";
import { getOAuthHeaders } from "../constants/oAuthValidation";
import useAuthStore from "../stores/useAuthStore";
import useReferenceStore from "../stores/useReferenceStore";
import useSessionStore from "../stores/useSessionStore";
import env from "../config/env";
import { applyAuthInterceptors } from "./authInterceptor";
import { applyHealthInterceptors } from "../utils/healthReporter";

// Initial baseURL — overridden per-request by tenant interceptors below
const genericUrl = process.env.REACT_APP_genericUrl;
const signinUrl = process.env.REACT_APP_signInUrl;
const genericInstance = axios.create();
genericInstance.defaults.baseURL = genericUrl;
genericInstance.defaults.method = "POST";
genericInstance.defaults.headers.post["Content-Type"] = "application/json";

// Dynamic tenant baseURL interceptor
genericInstance.interceptors.request.use((config) => {
  const url = window.__CW_TENANT_URLS__?.REACT_APP_genericUrl;
  if (url) config.baseURL = url;
  return config;
});

// Auth interceptors: token attachment + 401 refresh/retry
applyAuthInterceptors(genericInstance);

// Health tracking interceptors (latency + success/failure)
applyHealthInterceptors(genericInstance);

const signinInstance = axios.create();
signinInstance.defaults.baseURL = signinUrl;
signinInstance.defaults.method = "POST";

// Dynamic tenant baseURL interceptor for signin
signinInstance.interceptors.request.use((config) => {
  const url = window.__CW_TENANT_URLS__?.REACT_APP_signInUrl;
  if (url) config.baseURL = url;
  return config;
});

signinInstance.interceptors.request.use(
  (config) => {
    const authHeaders = getOAuthHeaders() || {};
    const { access_token } = authHeaders;
    if (access_token) {
      config.headers.Authorization = `${access_token}`;
    }
    config.headers["app_code"] = "CW-POS";
    config.headers["app_version"] = env.releaseNo;
    config.headers["Content-Type"] = "application/json";
    config.baseURL = window.__CW_TENANT_URLS__?.REACT_APP_signInUrl || signinUrl;
    config.method = "POST";
    config.async = true;
    config.crossDomain = true;
    return config;
  },
  (error) => Promise.reject(error)
);

signinInstance.interceptors.response.use(
  (response) => response,
  async (error) => Promise.reject(error)
);

// Health tracking interceptors (latency + success/failure)
applyHealthInterceptors(signinInstance);

const syncDocTypes = async () => {
  const docTypeQuery = {
    query: `mutation { executeAPIBuilder(apiBuilderId:"66f407348e3f1f618d89f888", params: "{}")} `,
  };
  try {
    const docTypesResponse = await genericInstance({
      data: docTypeQuery,
    });
    //   const docTypesResponse = await Axios.post(CWCoreServicesUrl, docTypeQuery, { headers: headers }, { async: true }, { crossDomain: true });
    if (docTypesResponse.status === 200) {
      let docTypesData = JSON.parse(docTypesResponse?.data?.data?.executeAPIBuilder);
      await db.docTypesData.clear();
      await db.docTypesData.bulkAdd(docTypesData);
    }
  } catch (error) {
    console.error("Error fetching doc types:", error);
    if (error.response) {
    } else if (error.request) {
    } else {
    }
  }
};

const syncRFIDData = async (syncId) => {
  const lastUpdatedTime = useSessionStore.getState().lastUpdatedTime;
  let syncStartTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
  const latestIncrementalSync = await db.dataSyncSummary.where("syncType").equals("Full Sync").reverse().sortBy("syncEndTime");
  const value = { tag_value: "", lastSyncTime: `${lastUpdatedTime ? `"${lastUpdatedTime}"` : null}` };
  const newStringifiedFields = JSON.stringify(value).replace(/\\"/g, '\\"');
  const newStringRequest = JSON.stringify(newStringifiedFields);
  const todaySalesInput = {
    query: `mutation { executeAPIBuilder(apiBuilderId:"64d323e6291b3b33ff234dd9", params: ${newStringRequest}) }`,
  };

  return new Promise(async (resolve, reject) => {
    try {
      const response = await genericInstance({
        data: todaySalesInput,
      });

      const executeAPIBuilder = response.data.data.executeAPIBuilder;
      const newResponse = JSON.parse(executeAPIBuilder) || [];

      if (newResponse.length > 0) {
        let syncEndTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
        if (lastUpdatedTime) {
          let recordsData = 0;
          await Promise.all(
            newResponse.map(async (item) => {
              const existingTag = await db.rfidData.where("tag_value").equals(item.tag_value).toArray();
              if (existingTag.length > 0) {
                recordsData = recordsData + 1;
                await db.rfidData.update(item.tag_value, item);
              } else {
                recordsData = recordsData + 1;
                await db.rfidData.add(item);
              }
            })
          );
          db.dataSyncSummary
            .where("syncId")
            .equals(syncId)
            .modify((event) => {
              event.type = { ...event.type, rfidData: { syncStartTime: syncStartTime, syncEndTime, records: recordsData } };
              event.records = (event.records ? event.records : 0) + recordsData;
            });
          if (latestIncrementalSync?.length > 0) {
            db.dataSyncSummary
              .where("syncId")
              .equals(latestIncrementalSync[0].syncId)
              .modify((event) => {
                event.type = {
                  ...event.type,
                  rfidData: { incremntalSyncStart: syncStartTime, incremntalSyncEnd: syncEndTime, incremntalSyncRecods: recordsData, ...event.type.rfidData },
                };
              });
          }
        } else {
          await db.rfidData.clear();
          await db.rfidData.bulkPut(newResponse);
          db.dataSyncSummary
            .where("syncId")
            .equals(syncId)
            .modify((event) => {
              event.type = { ...event.type, rfidData: { syncStartTime: syncStartTime, syncEndTime, records: newResponse.length } };
              event.records = (event.records ? event.records : 0) + newResponse.length;
            });
        }
      } else {
      }
      resolve(); // Resolve the Promise after successful execution
    } catch (error) {
      reject(error); // Reject the Promise if an error occurs
    }
  });
};

const syncRFIDScanConfig = async (tillData) => {
  const value = {};
  const newStringifiedFields = JSON.stringify(value).replace(/\\"/g, '\\"');
  const newStringRequest = JSON.stringify(newStringifiedFields);
  const todaySalesInput = {
    query: `mutation { executeAPIBuilder(apiBuilderId:"670520c68a26395542b489ee", params: ${newStringRequest}) }`,
  };

  return new Promise(async (resolve, reject) => {
    try {
      const response = await genericInstance({
        data: todaySalesInput,
      });

      const executeAPIBuilder = response?.data?.data?.executeAPIBuilder;
      useReferenceStore.getState().setPosScanConfigurations(executeAPIBuilder || []);
      resolve(); // Resolve the Promise after successful execution
    } catch (error) {
      reject(error); // Reject the Promise if an error occurs
    }
  });
};

const getRetailStores = async () => {
  try {
    const queryPayload = {
      query: `
        mutation {
          executeAPIBuilder(apiBuilderId: "69049a7305231e306432376b", params: "{}")
        }
      `,
    };

    const response = await genericInstance({ data: queryPayload });
    const rawData = response.data.data.executeAPIBuilder;

    // Parse if string, else use directly
    const storesData = typeof rawData === "string" ? JSON.parse(rawData) : rawData;

    return storesData; // Array of store objects
  } catch (error) {
    console.error("Error fetching retail stores:", error);
    return [];
  }
};

const syncSalesRepData = async () => {
  const tillData = useAuthStore.getState().tillData;
  const value = { cs_bunit_id: tillData.tillAccess.csBunit.csBunitId };
  const newStringifiedFields = JSON.stringify(value).replace(/\\"/g, '\\"');
  const newStringRequest = JSON.stringify(newStringifiedFields);
  const salesRepData = {
    query: `mutation { executeAPIBuilder(apiBuilderId:"669fa7b4befce80600118eef", params: ${newStringRequest})}`,
  };

  return new Promise(async (resolve, reject) => {
    try {
      const response = await genericInstance({
        data: salesRepData,
      });

      const executeAPIBuilder = response.data.data.executeAPIBuilder;
      const newResponse = JSON.parse(executeAPIBuilder) || [];
      await db.salesRep.clear();
      await db.salesRep.bulkPut(newResponse);
      resolve(); // Resolve the Promise after successful execution
    } catch (error) {
      reject(error); // Reject the Promise if an error occurs
    }
  });
};

const getStates = async () => {
  const query = {
    query: `query {
      searchField(
        windowId: "7001",
        ad_tab_id: "40F4CF4094994C54A5867DA26D032A9E",
        ad_field_id: "F4E1625F8D8D412C8C3741F117D6A1C5",
        searchField: "",
        dependent: null
      )
    }`,
  };

  try {
    const getStatesResponse = await genericInstance({
      data: query,
    });
    const rawData = getStatesResponse.data.data.searchField;
    const parsed = JSON.parse(rawData);
    const states = parsed.searchData;
    useReferenceStore.getState().setStatesData(states);
    return getStatesResponse;
  } catch (error) {
    console.error("Error fetching getStates:", error);

    if (error.response) {
    } else if (error.request) {
    } else {
    }
  }
};

const getCountries = async () => {
  const query = {
    query: `query {
      searchField(
        windowId: "7000",
        ad_tab_id: "A026B23FEDDE46EAAED076DBA5FEA545",
        ad_field_id: "082468868E9E4281B9F724F1A81E55F7",
        searchField: "",
        dependent: null
      )
    }`,
  };

  try {
    const getCountriesResponse = await genericInstance({
      data: query,
    });
    const rawData = getCountriesResponse.data.data.searchField;
    const parsed = JSON.parse(rawData);
    const countries = parsed.searchData;
    useReferenceStore.getState().setCountriesData(countries);
    return getCountriesResponse;
  } catch (error) {
    console.error("Error fetching getCountries:", error);

    if (error.response) {
    } else if (error.request) {
    } else {
    }
  }
};

const verifyOtpForLogin = async (data) => {
  try {
    const payload = {
      username: data.username,
      otpCode: data.otp,
      userActivityLogId: data.userActivityLogId,
    };

    const headers = {
      "Content-Type": "application/json",
    };

    // Read dynamically for tenant-aware resolution
    const currentVerifyOtpUrl = process.env.REACT_APP_verifyOtpUrl;
    const response = await axios.post(currentVerifyOtpUrl, payload, { headers: headers });
    return response;
  } catch (error) {
  }
};

const getGlobalParameters = async () => {
  const query = {
    query: `query { getGlobalParameters }`,
  };
  try {
    const getGlobalParametersResponse = await genericInstance({
      data: query,
    });
    return getGlobalParametersResponse;
  } catch (error) {
    console.error("Error fetching getGlobalParameters:", error);
    if (error.response) {
    } else if (error.request) {
    } else {
    }
  }
};

const signIn = async (payload) => {
  try {
    const signInResponse = await signinInstance({
      data: payload,
    });
    return signInResponse;
  } catch (error) {
    console.error("Error while sign in:", error);
    if (error.response) {
    } else if (error.request) {
    } else {
    }
    throw error;
  }
};

const getInventoryData = async (data) => {
  try {
    const response = await genericInstance({
      data,
    });
    return response;
  } catch (error) {
    console.error("Error fetching get inventory data:", error);
    if (error.response) {
    } else if (error.request) {
    } else {
    }
  }
};

const getWarehouseData = async (data) => {
  try {
    const response = await genericInstance({
      data,
    });
    return response;
  } catch (error) {
    console.error("Error fetching get warehouse data:", error);
    if (error.response) {
    } else if (error.request) {
    } else {
    }
  }
};

const productPriceOnScan = async (data) => {
  try {
    const response = await genericInstance({
      data,
    });
    return response;
  } catch (error) {
    console.error("Error in product price on scan:", error);
    if (error.response) {
    } else if (error.request) {
    } else {
    }
  }
};

const getBomProducts = async (data) => {
  try {
    const response = await genericInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in get bom products");
  }
};

const getChooseProduct = async (data) => {
  try {
    const response = await genericInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in get choose product");
  }
};

const makeToStock = async (data) => {
  try {
    const response = await genericInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in make to stock");
  }
};

const makeToOrder = async (data) => {
  try {
    const response = await genericInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in make to order");
  }
};

const getSearchedProducts = async (data) => {
  try {
    const response = await genericInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in get searched products");
  }
};

const getOtherCharges = async (data) => {
  try {
    const response = await genericInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in get other charges");
  }
};

const getStoreBins = async (data) => {
  try {
    const response = await genericInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in get store bins");
  }
};

const getSupplier = async (data) => {
  try {
    const response = await genericInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in get supplier data");
  }
};

const getSupplierProducts = async (data) => {
  try {
    const response = await genericInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in get supplier products data");
  }
};

const getWorkflowRules = async (data) => {
  try {
    const response = await genericInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in get work flow rules");
  }
};

const getFinancialAccounts = async (data) => {
  try {
    const response = await genericInstance({
      data: data,
    });
    const rawData = response?.data?.data?.executeAPIBuilder;
    const financialAccounts = typeof rawData === "string" ? JSON.parse(rawData) : rawData;
    if (financialAccounts?.length) {
      await db.getFinancialAccounts.clear();
      await db.getFinancialAccounts.bulkPut(financialAccounts);
    }
    return response;
  } catch (error) {
    console.error(error, "Error in get financial accounts");
  }
};

export {
  syncDocTypes,
  syncRFIDData,
  syncRFIDScanConfig,
  syncSalesRepData,
  getGlobalParameters,
  verifyOtpForLogin,
  signIn,
  getInventoryData,
  getWarehouseData,
  productPriceOnScan,
  getBomProducts,
  getChooseProduct,
  makeToStock,
  makeToOrder,
  getSearchedProducts,
  getOtherCharges,
  getStoreBins,
  getSupplier,
  getSupplierProducts,
  getWorkflowRules,
  getRetailStores,
  getStates,
  getCountries,
  getFinancialAccounts,
};
